import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Settings as SettingsIcon, User, Moon, Sun } from 'lucide-react';
import { FormField, Card, Button } from '../ui';

const Settings = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <SettingsIcon className="text-primary-600 dark:text-primary-400" size={32} />
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your account and preferences
          </p>
        </div>
      </div>

      {/* Profile Section */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <User className="text-primary-600 dark:text-primary-400" size={24} />
          <h2 className="text-xl font-semibold text-primary-900 dark:text-primary-50">
            Profile Information
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <FormField
            label="First Name"
            name="firstName"
            type="text"
            value={user?.firstName || ''}
            disabled
          />
          <FormField
            label="Last Name"
            name="lastName"
            type="text"
            value={user?.lastName || ''}
            disabled
          />
          <FormField
            label="Email"
            name="email"
            type="email"
            value={user?.email || ''}
            disabled
          />
          <FormField
            label="Role"
            name="role"
            type="text"
            value={user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1) || ''}
            disabled
          />
        </div>
      </Card>

      {/* Preferences Section */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          {theme === 'dark' ? (
            <Moon className="text-primary-600 dark:text-primary-400" size={24} />
          ) : (
            <Sun className="text-primary-600 dark:text-primary-400" size={24} />
          )}
          <h2 className="text-xl font-semibold text-primary-900 dark:text-primary-50">
            Appearance
          </h2>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="font-medium text-primary-900 dark:text-primary-50">Theme</p>
            <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
              Switch between light and dark mode
            </p>
          </div>
          <Button
            onClick={toggleTheme}
            variant="secondary"
            leftIcon={theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          >
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </Button>
        </div>
      </Card>

      {/* Security Section */}
      <Card>
        <h2 className="text-xl font-semibold text-primary-900 dark:text-primary-50 mb-4">
          Security
        </h2>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
            <div>
              <p className="font-medium text-primary-900 dark:text-primary-50">
                Password
              </p>
              <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
                Last changed: Never
              </p>
            </div>
            <Button variant="secondary">Change Password</Button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-primary-50 dark:bg-primary-900/50 rounded-lg">
            <div>
              <p className="font-medium text-primary-900 dark:text-primary-50">
                Two-Factor Authentication
              </p>
              <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
                Add an extra layer of security
              </p>
            </div>
            <Button variant="secondary">Enable</Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Settings;

